/**
 * 🔒 A page whose code chunk is gone reloads once, and never twice in a row
 * (piece 1; decision 1460). A tab left open across a deploy asks for a chunk
 * the new build no longer carries; the proxy answers it with a plain 404, and
 * Vite raises `vite:preloadError` on the window. Reloading fetches the new
 * page, whose chunk names are the new build's.
 *
 * 🔨 The guard is a stamp in session storage: a second failure inside the
 * window is let through, so the router draws its error and the tab never
 * loops. A store that is absent, blocked or throws is no guard at all, so the
 * page does not reload and the router's error shows at once.
 *
 * 💰 A second deploy inside the window in the same tab draws the router's error
 * rather than reloading, and the person reloads by hand.
 */
export const CHUNK_RELOAD_STORAGE_KEY = 'bms2.chunkReloadAt';

/** How long a reload guards against the next one: longer than any reloaded page takes to ask for its chunks. */
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

/** What the guard reads and does, so a test can hand it a store and a reload of its own. */
export interface ChunkReloadHost {
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
  now: () => number;
  /** Whether the browser reports a connection; a reload while it reports none lands on the browser's own error page. */
  online?: () => boolean;
}

/** Answers one `vite:preloadError`: true when it reloaded the page, false when it let the error through. */
export function reloadOnceForMissingChunk(event: Pick<Event, 'preventDefault'>, host: ChunkReloadHost): boolean {
  // 🔨 Offline, the failure is the connection's and not a deploy's: the page is kept, and the group's own panel offers Try again (decision 10580).
  if (host.online && !host.online()) return false;
  if (!host.storage) return false;
  try {
    const last = Number(host.storage.getItem(CHUNK_RELOAD_STORAGE_KEY) ?? '');
    if (Number.isFinite(last) && last > 0 && host.now() - last < CHUNK_RELOAD_WINDOW_MS) return false;
    host.storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(host.now()));
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return false;
  }
  // Vite rethrows the error unless the event is cancelled, and the reload is what answers it here.
  event.preventDefault();
  host.reload();
  return true;
}

function sessionStore(target: Window): Storage | null {
  try {
    return target.sessionStorage;
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return null;
  }
}

/** Listens on the window for the chunk failure Vite reports, from the entry, before the first render. */
export function installChunkReload(target: Window = window): void {
  target.addEventListener('vite:preloadError', (event) => {
    reloadOnceForMissingChunk(event, { storage: sessionStore(target), reload: () => target.location.reload(), now: () => Date.now(), online: () => target.navigator.onLine !== false });
  });
}
